import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { VatAmountCalculator } from '@zaoblako/shared/common/classes/vat.amount.calculator';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { TaskFindInterface } from '@zaoblako/shared/modules/task/dtos/task/task-find.dto';
import type { TaskFormInterface, TaskFormOperation } from '@zaoblako/shared/modules/task/dtos/task-form.dto';
import type { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { TaskStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/task.status.type.enum';
import { BasisClientService } from '@zaoblako/web/core/services/basis/basis-client.service';
import { ApprovalSchemeService } from '@zaoblako/web/core/services/budget/approval-scheme.service';
import { BankService } from '@zaoblako/web/core/services/catalog/bank.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { FilialService } from '@zaoblako/web/core/services/finance/filial.service';
import { NdsService } from '@zaoblako/web/core/services/finance/nds.service';
import { UnitService } from '@zaoblako/web/core/services/finance/unit.service';
import { BrsPrefixService } from '@zaoblako/web/core/services/prefix/brs-prefix.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { TaskPriorityService } from '@zaoblako/web/core/services/task/task-priority.service';
import { TaskStatusService } from '@zaoblako/web/core/services/task/task-status.service';
import { TaskTemplateService } from '@zaoblako/web/core/services/task/task-template.service';
import { TaskService } from '@zaoblako/web/core/services/task/task.service';
import { TaskEditPermissions } from '@zaoblako/web/core/types/task-edit-permissions.type';
import { TaskFormComponent } from '@zaoblako/web/modules/payments/dumbs/task-form/task-form.component';
import { resolveOrganizationCurrencies } from '@zaoblako/web/modules/payments/utils/resolve-organization-currencies.util';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { PaymentsSettingsService } from '@zaoblako/web/core/services/finance/payments-settings.service';
import type { AgentFindInterface } from '@zaoblako/shared/modules/ai/dtos/agent/agent-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';

import moment from 'moment-timezone';
import { cloneDeep, isNil } from 'lodash';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-task-form-sidebar',
  templateUrl: './task-form-sidebar.component.html',
  imports: [CommonModule, TaskFormComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskFormSidebarComponent implements OnInit {
  @Input() public readonly index: number;
  public task = <TaskFormInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public activeStep = 0;

  public organizations: CompanyFindInterface[] = [];
  public filials: FilialFindInterface[] = [];
  public units: UnitFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];
  public priorities: TaskPriorityFindInterface[] = [];
  public nds: NdsFindInterface[] = [];
  public banks: BankFindInterface[] = [];
  public statuses: TaskStatusFindInterface[] = [];
  /** Пока грузятся статусы и выставляется тип заявки по умолчанию. */
  public kindLoading = true;
  public schemes: ApprovalSchemeFindInterface[] = [];
  public currencies: CurrencyFindInterface[] = [];
  public bankDetails: BankDetailsFindInterface[] = [];
  public contragentBankDetails: BankDetailsFindInterface[] = [];
  public defaultAgent: AgentFindInterface;

  public operation: TaskFormOperation = 'create';

  public taskEditPermissions: TaskEditPermissions;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly taskService: TaskService,
    private readonly taskStatusService: TaskStatusService,
    private readonly companyService: CompanyService,
    private readonly filialService: FilialService,
    private readonly unitService: UnitService,
    private readonly directionService: DirectionService,
    private readonly taskPriorityService: TaskPriorityService,
    private readonly ndsService: NdsService,
    private readonly bankService: BankService,
    private readonly errorService: ErrorService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly taskTemplateService: TaskTemplateService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly approvalSchemeService: ApprovalSchemeService,
    private readonly dealService: DealService,
    private readonly contragentService: ContragentService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly basisClientService: BasisClientService,
    private readonly brsPrefixService: BrsPrefixService,
    private readonly paymentsSettingsService: PaymentsSettingsService
  ) {}

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    const copyId = this.formStoreService.getState.forms[this.index]?.data?.copyId;
    const templateId = this.formStoreService.getState.forms[this.index]?.data?.templateId;
    const dealId = this.formStoreService.getState.forms[this.index]?.data?.dealId;
    const formData = this.formStoreService.getState.forms[this.index]?.data;

    if (formData?.contragent) {
      this.task.contragent = formData.contragent;
    }
    if (formData?.type) {
      this.task.type = formData.type;
    }
    if (!isNil(formData?.amount)) {
      this.task.amount = formData.amount;
    }

    if (templateId || copyId) {
      this.formStoreService.setLoading(true, this.index);
    }

    if (copyId) {
      this.taskService
        .findById<TaskFindInterface>(copyId)
        .pipe(
          switchMap((task) => {
            return forkJoin([
              task.organization
                ? this.basisClientService.bankDetailsSearch({
                    companyIds: [task.organization?.id],
                    onlyForCompanies: true
                  })
                : of(<BankDetailsFindInterface[]>[]),
              task.contragent
                ? this.basisClientService.bankDetailsSearch({
                    contragentIds: [task.contragent?.id]
                  })
                : of(<BankDetailsFindInterface[]>[])
            ]).pipe(switchMap(([bankDetails, contragentBankDetails]) => {
              return of({ task, bankDetails, contragentBankDetails });
            }));
          }),
          tap(() => this.formStoreService.setLoading(false, this.index))
        )
        .subscribe(({ task, bankDetails, contragentBankDetails }) => {
          this.task = task as unknown as TaskFormInterface;
          this.bankDetails = bankDetails;
          this.contragentBankDetails = contragentBankDetails;
          this.task.status = null;

          this.syncCurrenciesFromOrganization();
          if (isNaN(this.task.vat)) {
            this.task.vat = 0;
          }
          if (this.task.nds?.amount !== null && this.task.nds?.amount !== undefined) {
            this.task.vat = VatAmountCalculator.getInstance().getVatAmount(
              this.task.amount || 0,
              this.task.nds?.amount || 0
            );
          }

          this.task = { ...this.task };
          this.cdRef.markForCheck();
        });
    }

    if (templateId) {
      this.taskTemplateService
        .getTaskFormByTemplate(templateId)
        .pipe(
          switchMap((data) => {
            return forkJoin([
              data.task?.organization
                ? this.basisClientService.bankDetailsSearch({
                    companyIds: [data.task?.organization?.id],
                    onlyForCompanies: true
                  })
                : of(<BankDetailsFindInterface[]>[]),
              data.task?.contragent
                ? this.basisClientService.bankDetailsSearch({
                    contragentIds: [data.task?.contragent?.id]
                  })
                : of(<BankDetailsFindInterface[]>[])
            ]).pipe(switchMap(([bankDetails, contragentBankDetails]) => {
              return of({ data, bankDetails, contragentBankDetails });
            }));
          }),
          tap(() => this.formStoreService.setLoading(false, this.index))
        )
        .subscribe(({ data, bankDetails, contragentBankDetails }) => {
          this.task = data.task;
          this.bankDetails = bankDetails;
          this.contragentBankDetails = contragentBankDetails;
          this.activeStep = 1;

          this.syncCurrenciesFromOrganization();
          if (isNaN(this.task.vat)) {
            this.task.vat = 0;
          }

          this.task = { ...this.task };
          this.cdRef.markForCheck();
        });
    }

    if (!isNaN(dealId)) {
      this.dealService
        .findById<DealFindInterface>(dealId)
        .pipe(
          tap((deal) => {
            const dealFields: Array<keyof DealFindInterface> = ['amount', 'filial', 'direction', 'unit'];
            dealFields.forEach((key) => {
              this.task[key] = cloneDeep(deal[key]);
            });
            this.task.deal = deal;
            this.cdRef.markForCheck();
          }),
          switchMap((deal) =>
            this.contragentService.getAll({
              limit: 1,
              offset: 0,
              search: deal.contragent.name,
              select: ['id', 'name', 'inn', 'kpp', 'personType', 'bankDetails']
            })
          )
        )
        .subscribe((data) => {
          if (data.items.length) {
            this.task.contragent = data.items[0];
          }

          this.task = { ...this.task };
          this.cdRef.markForCheck();
        });
    }

    forkJoin([
      this.taskStatusService.find<TaskStatusFindInterface>(),
      this.directionService.find<DirectionFindInterface>({
        kinds: [DirectionKindEnum.EXPENSE, DirectionKindEnum.INCOME_TAX, DirectionKindEnum.DIVIDENTS]
      }),
      this.paymentsSettingsService.getDefaultAgent()
    ]).subscribe({
      next: ([statuses, directions, defaultAgent]) => {
        this.directions = directions;
        this.defaultAgent = defaultAgent;
        this.statuses = statuses.filter((status) =>
          [TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL].includes(status.type)
        );

        if (!this.task.kind && !this.formStoreService.getState.forms[this.index]?.isEdit) {
          this.task.kind = TaskKindEnum.PAYMENT_TO_SUPPLIER;
        }
        this.kindLoading = false;
        this.task = { ...this.task };
        this.cdRef.markForCheck();
      },
      error: () => {
        this.kindLoading = false;
        this.cdRef.markForCheck();
      }
    });

    forkJoin([
      this.companyService.findMany(),
      this.filialService.findPage(),
      this.unitService.findPage(),
      this.taskPriorityService.find<TaskPriorityFindInterface>(),
      this.ndsService.find<NdsFindInterface>(),
      this.approvalSchemeService.findPage({ onlyWithExplicitAccess: true })
    ]).subscribe(([{ items: organizations }, { items: filials }, { items: units }, priorities, nds, { items: schemes }]) => {
      this.organizations = organizations;
      this.filials = filials;
      this.units = units;
      this.priorities = priorities;
      this.nds = nds;
      this.schemes = schemes;

      if (this.task.filial) {
        this.task.filial = this.filials?.find((filial) => filial?.id === this.task.filial?.id);
      }

      const isCreateForm = !this.formStoreService.getState.forms[this.index]?.isEdit;
      this.normalizeTaskFormDates(isCreateForm);

      if (isCreateForm) {
        if (!this.task.vat) {
          this.task.vat = 0;
        }
        if (!this.task.priority) {
          this.task.priority = this.priorities.find((priority) => priority.name === 'Средний');
        }
        if (!this.task.type) {
          this.task.type = TaskPaymentTypeEnum.BANK;
        }
        if (!this.task.organization && this.organizations.length === 1) {
          this.task.organization = this.organizations[0];
          this.changeOrganization(this.organizations[0]);
        }
        if (!this.task.unit && this.units.length === 1) {
          this.task.unit = this.units[0];
        }
        if (!this.task.filial && this.filials.length === 1) {
          this.task.filial = this.filials[0];
        }
        if (!this.task.subject) {
          this.task.subject = 'Оплата по счет №.  от ';
        }
        if (!this.task.responsible) {
          this.task.responsible = this.authService.currentUser;
        }
        this.initializeApprovalScheme();
      }
      if (this.task.organization?.id) {
        this.syncCurrenciesFromOrganization();
      }
      this.task = { ...this.task };
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.formStoreService.setLoading(true, this.index);

      forkJoin([
        this.taskService.findById<TaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId),
        this.approvalSchemeService.findPage()
      ])
        .pipe(
          switchMap(([task, { items: schemes }]) => {
            return forkJoin([
              task.organization
                ? this.basisClientService.bankDetailsSearch({
                    companyIds: [task.organization?.id],
                    onlyForCompanies: true
                  })
                : of(<BankDetailsFindInterface[]>[]),
              task.contragent
                ? this.basisClientService.bankDetailsSearch({
                    contragentIds: [task.contragent?.id]
                  })
                : of(<BankDetailsFindInterface[]>[])
            ]).pipe(switchMap(([bankDetails, contragentBankDetails]) => {
              return of({ task, schemes, bankDetails, contragentBankDetails });
            }));
          })
        )
        .subscribe(({ task, schemes, bankDetails, contragentBankDetails }) => {
          this.operation = 'update';
          this.bankDetails = bankDetails;
          this.contragentBankDetails = contragentBankDetails;

          this.task = task;
          this.task.scheme = schemes?.find((scheme) => this.task.scheme?.id === scheme?.id);
          this.syncCurrenciesFromOrganization();
          const { permissions } = this.taskService.getTaskPermissions(task);
          if (!task.canEdit) {
            this.formStoreService.closeSidebar(this.index);
            this.router
              .navigate(['/payments/task/card/', this.brsPrefixService.getPrefixedNumber(task)])
              .catch(console.error);
          } else {
            this.taskEditPermissions = permissions;
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          }
        });
    }

    this.taskEditPermissions = this.taskService.getTaskPermissions(
      this.task as unknown as TaskFindInterface
    ).permissions;
    Object.keys(this.taskEditPermissions).forEach((prop) => (this.taskEditPermissions[prop] = true));
  }

  public searchBank(name: string) {
    this.bankService.search({ search: name }).subscribe((banks) => {
      this.banks = banks;
      this.cdRef.markForCheck();
    });
  }

  public saveTask(task: TaskFormInterface) {
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      const savingMethod = () => {
        this.formStoreService.setLoading(true, this.index);

        this.taskService.update(task).subscribe({
          next: (newTask) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Заявка ${this.brsPrefixService.getPrefixedNumber(newTask)} обновлена`);
            this.formStoreService.saveNext(newTask, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      };

      if (task.amount !== task.orders?.reduce((sum, order) => sum + order.amount, 0)) {
        this.confirmDialogService.confirm({
          message: 'Сумма заявки и сумма платежных поручений отличаются друг от друга.<br />Продолжить сохранение?',
          accept: () => {
            savingMethod();
          }
        });
        return;
      }

      savingMethod();
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.taskService.createTask(task).subscribe({
        next: (newTask) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Заявка создана');
          if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
            this.formStoreService.closeSidebar(this.index);
            this.router
              .navigate(['/payments/task/card/' + this.brsPrefixService.getPrefixedNumber(newTask)])
              .catch(console.error);
          } else {
            this.formStoreService.saveNext(newTask, this.index);
          }
        },
        error: () => {
          this.activeStep = 1;
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public deleteTask(task: TaskFormInterface) {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить заявку ${this.brsPrefixService.getPrefixedNumber(
        task as TaskFindInterface
      )}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);

        this.taskService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(
            `Заявка ${this.brsPrefixService.getPrefixedNumber(task as TaskFindInterface)} удалена`
          );
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public changeOrganization(organization?: CompanyFindInterface) {
    const org =
      this.organizations.find((company) => company.id === organization?.id) ??
      organization ??
      this.task.organization;

    if (!org?.id) {
      this.currencies = [];
      this.bankDetails = [];
      this.task.organization = null;
      this.task.currency = null;
      this.task.organizationBankDetails = null;
      this.cdRef.markForCheck();
      return;
    }

    const existingOrganizationBankDetails =
      organization?.id && this.task.organization?.id === organization.id
        ? this.task.organizationBankDetails
        : null;

    this.task.organization = org;
    this.bankDetails = [];
    this.task.currency = null;
    this.task.organizationBankDetails = null;

    this.syncCurrenciesFromOrganization();
    if (this.currencies.length) {
      setTimeout(() => {
        this.task.currency = this.currencies.find((currency) => currency.isDefault);
        this.task['rate'] = this.task.currency?.latestRate?.rate;
        this.cdRef.markForCheck();
      });
    }

    this.loadOrganizationBankDetails(org.id, org, existingOrganizationBankDetails);
  }

  private syncCurrenciesFromOrganization = (): void => {
    this.currencies = resolveOrganizationCurrencies(
      this.task.organization,
      this.organizations,
      this.task.currency
    );

    const organizationFromDirectory = this.organizations.find(({ id }) => id === this.task.organization?.id);
    if (organizationFromDirectory && this.currencies.length) {
      this.task.organization = {
        ...this.task.organization,
        currencies: this.currencies
      };
    }
  };

  public reloadOrganizationBankDetailsIfNeeded() {
    if (this.task.organization?.id && !this.bankDetails?.length) {
      this.loadOrganizationBankDetails(
        this.task.organization.id,
        this.task.organization,
        this.task.organizationBankDetails
      );
    }
  }

  private loadOrganizationBankDetails(
    companyId: number,
    organization?: CompanyFindInterface,
    existingOrganizationBankDetails?: BankDetailsFindInterface
  ) {
    this.basisClientService
      .bankDetailsSearch({
        companyIds: [companyId],
        onlyForCompanies: true
      })
      .subscribe((bankDetails) => {
        this.bankDetails = bankDetails;
        const picked = this.pickOrganizationBankDetails(
          this.bankDetails,
          organization,
          existingOrganizationBankDetails
        );
        this.task.organizationBankDetails = picked
          ? this.bankDetails.find((bankDetail) => bankDetail.id === picked.id) ?? picked
          : null;
        this.task = { ...this.task };
        this.cdRef.markForCheck();
      });
  }

  private pickOrganizationBankDetails(
    bankDetails: BankDetailsFindInterface[],
    organization?: CompanyFindInterface,
    existingOrganizationBankDetails?: BankDetailsFindInterface
  ): BankDetailsFindInterface | null {
    if (!bankDetails?.length) {
      return null;
    }

    if (existingOrganizationBankDetails?.id || existingOrganizationBankDetails?.checkingAccount) {
      const byId = bankDetails.find(
        (bankDetail) => bankDetail.id && bankDetail.id === existingOrganizationBankDetails.id
      );
      const byCheckingAccount = bankDetails.find(
        (bankDetail) =>
          !byId && bankDetail.checkingAccount === existingOrganizationBankDetails.checkingAccount
      );
      const restored = byId || byCheckingAccount;

      if (restored) {
        return restored;
      }
    }

    if (bankDetails.length === 1) {
      return bankDetails[0];
    }

    const defaultFromList = bankDetails.find((bankDetail) => !!bankDetail.isDefault);
    if (defaultFromList) {
      return defaultFromList;
    }

    const defaultId = organization?.bankDetails?.find((bankDetail) => bankDetail.isDefault)?.id;
    if (defaultId) {
      return bankDetails.find((bankDetail) => bankDetail.id === defaultId) ?? null;
    }

    return null;
  }

  public changeResponsibleUser(user: UserFindInterface) {
    this.task.responsible = user;
    this.initializeApprovalScheme();
  }

  private normalizeTaskFormDates = (applyCreateDefaults: boolean): void => {
    const taskDate = moment(this.task.date);

    if (taskDate.isValid()) {
      this.task.date = taskDate.toDate();
    } else if (applyCreateDefaults) {
      this.task.date = moment().toDate();
    }

    this.task.orders?.forEach((order) => {
      const orderDate = moment(order.date);

      if (orderDate.isValid()) {
        order.date = orderDate.toDate();
        return;
      }

      if (applyCreateDefaults) {
        order.date = moment().toDate();
      }
    });
  };

  private initializeApprovalScheme() {
    if (!this.task.scheme) {
      this.task.scheme = this.schemes?.find((scheme) => scheme.isDefault);
    }

    if (!this.task.scheme && this.task.responsible?.approvalScheme) {
      this.task.scheme = this.task.responsible.approvalScheme;
    }

    if (this.task.scheme) {
      this.task.scheme = this.schemes?.find((scheme) => this.task.scheme?.id === scheme?.id);
      this.changeScheme(this.task.scheme);
    }
    this.cdRef.markForCheck();
  }

  private changeScheme(scheme: ApprovalSchemeFindInterface) {
    const newUserAuditors = scheme?.userAuditors || [];
    const newUserGroupAuditors = scheme?.userGroupAuditors || [];
    this.task = {
      ...this.task,
      scheme: scheme,
      userAuditors: newUserAuditors,
      userGroupAuditors: newUserGroupAuditors
    };
    this.cdRef.markForCheck();
  }

  public onContragentChange(contragent: ContragentFindInterface) {
    if (!contragent?.id) {
      this.task.contragentBankDetails = null;
      this.contragentBankDetails = [];
      this.cdRef.markForCheck();
      return;
    }

    this.basisClientService
      .bankDetailsSearch({
        contragentIds: [contragent.id]
      })
      .subscribe((bankDetails) => {
        const defaultBankDetails = bankDetails.length > 1
          ? bankDetails.find((item) => item.isDefault)
          : bankDetails[0];
        this.contragentBankDetails = bankDetails;

        let contragentBankDetails: BankDetailsFindInterface = null;
        if (this.task.contragentBankDetails?.id) {
          contragentBankDetails = bankDetails.find((item) => item?.id === this.task.contragentBankDetails.id) || defaultBankDetails;
        } else {
          contragentBankDetails = defaultBankDetails;
        }
        this.task.contragentBankDetails = contragentBankDetails;

        this.cdRef.markForCheck();
      });
  }
}
